import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PriceApprovalRange } from '../../../models/PriceApprovalRange';
import { PriceApprovalRangesService } from '../../../services/price-approval-ranges.service';
import { TableAction, TableColumn } from '../../../shared/components/table/table.component';

interface PriceApprovalRangeRow extends PriceApprovalRange {
  amountRangeLabel: string;
  flagsLabel: string;
  approversLabel: string;
  activeLabel: string;
}

@Component({
  selector: 'app-price-approval-ranges-list',
  templateUrl: './price-approval-ranges-list.component.html',
  styleUrls: ['./price-approval-ranges-list.component.css']
})
export class PriceApprovalRangesListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  loading = false;
  rows: PriceApprovalRangeRow[] = [];
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  errorMessage: string | null = null;

  columns: TableColumn[] = [
    { titulo: 'Regra', label: 'ruleName' },
    { titulo: 'Faixa (R$)', label: 'amountRangeLabel' },
    { titulo: 'Flags', label: 'flagsLabel' },
    { titulo: 'Aprovadores extras', label: 'approversLabel' },
    { titulo: 'Ativo', label: 'activeLabel' }
  ];
  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-info' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-warning' }
  ];

  constructor(
    private priceApprovalRangesService: PriceApprovalRangesService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openCreate(): void {
    this.router.navigate(['/price-approval/ranges/new']);
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    this.priceApprovalRangesService
      .getPaged(this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.rows = (res.items || []).map((item) => this.toRow(item));
          this.totalItems = res.totalItems;
          this.totalPages = res.totalPages;
          this.loading = false;
        },
        error: () => {
          this.errorMessage = 'Não foi possível carregar as faixas de aprovação.';
          this.rows = [];
          this.loading = false;
        }
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.load();
  }

  handleAction(event: { action: string; item: PriceApprovalRangeRow }): void {
    const ruleId = event?.item?.id;
    if (!ruleId) return;
    if (event.action === 'view') {
      this.router.navigate(['/price-approval/ranges', ruleId, 'view']);
      return;
    }
    if (event.action === 'edit') {
      this.router.navigate(['/price-approval/ranges', ruleId, 'edit']);
    }
  }

  private toRow(item: PriceApprovalRange): PriceApprovalRangeRow {
    const money = (n: number) =>
      Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const parts: string[] = [];
    if (item.requiresManagerApproval) parts.push('Gestor');
    if (item.requiredAdditionalApprovalsCount > 0) {
      parts.push(`${item.requiredAdditionalApprovalsCount} aprov. adicionais`);
    }
    if (item.skipApprovalWhenInRange) parts.push('Pula aprovação na faixa');
    if (item.requiresSequentialApproval) parts.push('Sequencial');
    const approvers = (item.additionalApprovers || [])
      .slice()
      .sort((a, b) => (a.approvalOrder ?? 0) - (b.approvalOrder ?? 0))
      .map((a) => a.staffName || `Staff #${a.staffId}`)
      .filter(Boolean);
    return {
      ...item,
      amountRangeLabel: `${money(item.minAmount)} — ${money(item.maxAmount)}`,
      flagsLabel: parts.length ? parts.join(' · ') : '—',
      approversLabel: approvers.length ? approvers.join(' → ') : '—',
      activeLabel: item.isActive ? 'Sim' : 'Não'
    };
  }
}
